import { useState, useCallback, useLayoutEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useResizeObserver } from '../../../hooks/useResizeObserver.js';
import { usePrintResizing } from '../../../hooks/usePrintResizing.js';
import { useTheme, usePrevious } from '@shopify/polaris-viz-core';

function useContainerBounds({
  onIsPrintingChange,
  scrollContainer,
  sparkChart
}) {
  const {
    chartContainer
  } = useTheme();
  const {
    ref,
    setRef,
    entry
  } = useResizeObserver();
  const previousEntry = usePrevious(entry);
  const [containerBounds, setContainerBounds] = useState(null);
  usePrintResizing({
    ref,
    setContainerBounds,
    onIsPrintingChange
  });
  const updateDimensions = useCallback(() => {
    if ((previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.contentRect.width) === (entry === null || entry === void 0 ? void 0 : entry.contentRect.width) && (previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.contentRect.height) === (entry === null || entry === void 0 ? void 0 : entry.contentRect.height) || entry == null) {
      return;
    }
    const {
      width,
      height
    } = entry.contentRect;
    const {
      x,
      y
    } = entry.target.getBoundingClientRect();
    const scrollY = scrollContainer == null ? window.scrollY : scrollContainer.scrollTop;
    setContainerBounds({
      width,
      height,
      x,
      y: y + scrollY
    });
  }, [entry, previousEntry === null || previousEntry === void 0 ? void 0 : previousEntry.contentRect, scrollContainer]);
  const debouncedUpdateDimensions = useDebouncedCallback(() => {
    updateDimensions();
  }, 100);
  useLayoutEffect(() => {
    updateDimensions();
    if (containerBounds === null) {
      setContainerBounds({
        width: 0,
        height: sparkChart ? chartContainer.sparkChartMinHeight : chartContainer.minHeight,
        x: 0,
        y: 0
      });
    }
    const isServer = typeof window === 'undefined';
    if (!isServer) {
      window.addEventListener('resize', debouncedUpdateDimensions);
    }
    return () => {
      if (!isServer) {
        window.removeEventListener('resize', debouncedUpdateDimensions);
      }
    };
  }, [entry, updateDimensions, debouncedUpdateDimensions, containerBounds, chartContainer.minHeight, sparkChart, chartContainer.sparkChartMinHeight]);
  const updateContainerBounds = useCallback(() => {
    if (ref == null) {
      return;
    }
    const scrollY = scrollContainer == null ? window.scrollY : scrollContainer.scrollTop;
    const bounds = ref.getBoundingClientRect();
    setContainerBounds(prev => {
      if (bounds.y === (prev === null || prev === void 0 ? void 0 : prev.y) && bounds.x === (prev === null || prev === void 0 ? void 0 : prev.x)) {
        return prev;
      }
      return {
        width: bounds.width,
        height: bounds.height,
        x: bounds.x,
        y: bounds.y + scrollY
      };
    });
  }, [ref, scrollContainer]);
  return {
    containerBounds,
    updateContainerBounds,
    setRef
  };
}

export { useContainerBounds };
